import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import api from "../lib/api";

export default function Courses() {
  const { t, i18n } = useTranslation();
  const [schools, setSchools] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [loadError, setLoadError] = useState(false);
  const fr = i18n.language === "fr";

  useEffect(() => {
    Promise.all([api.get("/learn/schools"), api.get("/learn/courses")])
      .then(([s, c]) => { setSchools(s.data); setCourses(c.data); })
      .catch(() => setLoadError(true));
  }, []);

  return (
    <div className="space-y-10" data-testid="courses-catalog">
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight fade-up">{t("courses.title")}</h1>
      {loadError && <p className="text-[#FF3B30]" data-testid="courses-error">{t("common.error")}</p>}
      {schools.map((school) => {
        const list = courses.filter((c) => c.school_id === school.id);
        if (list.length === 0) return null;
        return (
          <section key={school.id} className="fade-up-1" data-testid={`school-section-${school.slug}`}>
            <div className="flex items-baseline gap-3 mb-4">
              <h2 className="font-heading text-xl font-semibold">{fr ? school.name_fr : school.name_en}</h2>
              <span className="text-xs font-mono-v text-muted-foreground">{list.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((c) => (
                <div key={c.id} className="swiss-card overflow-hidden flex flex-col" data-testid={`course-card-${c.slug}`}>
                  <div className="h-28 bg-muted overflow-hidden">
                    <img src={c.cover_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex flex-wrap gap-1.5">
                      <span className={`text-[10px] font-mono-v font-semibold px-2 py-0.5 rounded-md ${c.status === "PUBLISHED" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {t(`courses.status.${c.status}`)}
                      </span>
                      {c.status === "PUBLISHED" && (
                        <span className="text-[10px] font-mono-v px-2 py-0.5 bg-muted rounded-md text-muted-foreground">
                          {t(`courses.policy.${c.enrollment_policy}`)}
                        </span>
                      )}
                      {c.data_class === "DEMO" && (
                        <span className="text-[10px] font-mono-v px-2 py-0.5 bg-zinc-100 rounded-md text-zinc-500">DEMO</span>
                      )}
                    </div>
                    <h3 className="font-heading font-semibold mt-3">{fr ? c.title_fr : c.title_en}</h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2 flex-1">{fr ? c.description_fr : c.description_en}</p>
                    <div className="mt-4 flex items-center justify-between">
                      {c.enrollment ? (
                        <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                          <CheckCircle2 size={14} />
                          {c.enrollment.completed_at ? t("courses.completed") : `${t("courses.enrolled")} · v${c.enrollment.version} · ${c.enrollment.progress_pct}%`}
                        </span>
                      ) : c.assigned ? (
                        <span className="text-xs font-medium text-[#002BF6]">{t("courses.assigned")}</span>
                      ) : <span />}
                      {(c.enrollment || c.can_enroll) && (
                        <Link to={`/courses/${c.id}`} data-testid={`course-open-${c.slug}`}
                          className="text-sm font-medium text-white bg-[#002BF6] px-4 py-1.5 rounded-md hover:bg-[#0024cc] transition-colors duration-200">
                          {c.enrollment ? t("courses.open") : t("courses.enroll")}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
